// «إثبات الدفع» (الدفعة الأولى) داخل شاشة إنشاء الحجز.
//
// لا قواعد دفع جديدة هنا: نفس طرق الدفع المعتمدة، ونفس الحقول المطلوبة لكل طريقة، ونفس التحقق
// الذي تستعمله شاشة الدفعات القائمة — والخادم يعيد فحص كل شيء على أي حال.
//
// والعملة ليست حقلًا يُدخله أحد: تتبع الطريقة. التحويل البنكي السعودي بالريال، وما عداه بالجنيه،
// والخادم هو من يكتبها. ولا تحويل بين العملتين ولا سعر صرف — الشاشة تقول ذلك للبياع صراحةً
// بدل أن يظنّ أن ريالًا سيُخصم من متبقٍّ بالجنيه.
//
// والدفعة **إجبارية على «جديد» و«مكرر»** (قرار المالك)، واختيارٌ في «شركة» و«تعديل»
// و«الغاء». وحيث تلزم يُفتح القسم من نفسه ويختفي مفتاحه. وإن اختيرت حيث لا تلزم، لزم
// استكمال بياناتها — ولا تُنشأ دفعة بصفر.
//
// الإثبات: النقدي إقرار استلام، وInstaPay/Wallet صورة التحويل. و**هو مطلوبٌ مع الدفعة**
// (قرار المالك: «إثبات الدفع إجباري»): الاعتماد يتطلّبه على كل حال منذ 0011، فدفعةٌ بلا
// إثبات تقف في قائمة المراجعة حتى يعود صاحبها بالصورة. أما رفعُ الملفّ نفسه فيقع **بعد**
// أن توجد الدفعة (multipart لا يدخل معاملة القاعدة)، وفشل الرفع لا يُبطل ما حُفظ — يُقال
// لصاحبه أن يعيده من تفاصيل الحجز.
import { useEffect, useState } from 'react';
import { upload } from '../lib/api.ts';
import { Alert, Field, Select, TextInput } from '../components/ui.tsx';
import { EGP, egpNum } from '../lib/format.ts';
import { ACCEPT_ATTR, PAYMENT_EVIDENCE_LOCATION_NOTE, humanSize, validateFile } from './attachments/fileRules.ts';
import { PAYMENT_METHODS, currencyAr, currencyInAr, currencyOfMethod, type PaymentMethodCode } from './payments/paymentLabels.ts';
import { DESTINATION_LABEL, needsDestination, usePaymentDestinations } from './payments/destinations.ts';
import type { LookupOption } from '../lib/api.ts';

export type PaymentMethod = PaymentMethodCode;

/** جزءٌ من المحصَّل بطريقةٍ واحدة — طريقته ومبلغه ودافعه وإثباته. */
export interface PaymentPart {
  method: PaymentMethod;
  amount: string;
  /**
   * «الفلوس دي راحت لحساب مين؟» — بدل اسم الدافع وحساب المصدر (0130، قرار المالك 26/09/2026: «اسم الدافع دى
   * ملهاش لازمه وكذلك حساب / عنوان instapay وحط مكانه Drop list»). للتحويلات وحدها.
   */
  destination: string;
  /** إثباتاتٌ عدّة (قرار المالك 23/09/2026: «اتأكد إنه يخلّيه يرفع أكتر من صورة عادي») */
  evidence: File[];
  /**
   * رسوم المحفظة (قرار المالك 02/10/2026: «لو مدفوع بتحويل محفظة لازم نحصّل من العميل 1% زيادة… اللي وصل لينا فعلًا 10000
 * بس… ممكن تسأله المبلغ البيع كام ورسوم فودافون كاش كام»): المبلغ كما في السكرينة، والرسوم وحدها، والحجز يُحسب على الفرق.
   */
  walletFee?: string;
}

export interface InitialPaymentDraft extends PaymentPart {
  enabled: boolean;
  /**
   * **أجزاءٌ بطرقٍ أخرى** (قرار المالك 26/09/2026: «اوقات العميل بيدفع على مصدرين مختلفين مثلا يدفع جزا
   * فيزا - المكتب وجزء اخر كاش … ال يرفع الحجز الجديد يدخلهم ف نفس الوقت مش لازم يثبت دفعه تانية»):
   * كلُّ جزءٍ دفعةٌ مستقلّة عند الخادم بطريقته وإثباته — فيرى الأدمن المصدرين بلا خلط. حتى أربعة أجزاء.
   */
  extra: PaymentPart[];
}

export const MAX_PAYMENT_PARTS = 4;
export const emptyPaymentPart = (method: PaymentMethod = 'CASH'): PaymentPart => ({
  method, amount: '', destination: '', evidence: [], walletFee: '',
});
export const emptyInitialPayment = (): InitialPaymentDraft => ({
  enabled: false, ...emptyPaymentPart('INSTAPAY'), extra: [],
});

/** الأجزاء كلّها بترتيبها: الأوّل ثم الإضافية. */
export const paymentParts = (p: InitialPaymentDraft): PaymentPart[] => [p, ...(p.extra ?? [])];

const MONEY_RE = /^\d{1,12}$/;

/** اسم الإثبات بلسان الطريقة — ما يطلبه الأدمن للاعتماد يختلف باختلافها. */
const EVIDENCE_LABEL: Record<string, string> = {
  CASH: 'إقرار الاستلام',
  VISA_OFFICE: 'إيصال ماكينة الفيزا',
  INDIRECT_INSTALLMENT: 'إثبات التقسيط',
};

/** تحقّق ما تحتاجه الطريقة — نفس قواعد شاشة الدفعات القائمة. */
export function validateInitialPayment(p: InitialPaymentDraft): Record<string, string> {
  if (!p.enabled) return {};
  const e = validatePart(p);
  // أخطاء الأجزاء الإضافية بمفتاحٍ يحمل رقمها: «extra0.amount» — فيُعرض الخطأ تحت خانته هو
  (p.extra ?? []).forEach((x, i) => {
    for (const [k, v] of Object.entries(validatePart(x))) e[`extra${i}.${k}`] = v;
  });
  return e;
}

function validatePart(p: PaymentPart): Record<string, string> {
  const e: Record<string, string> = {};
  if (!p.amount.trim()) e.amount = 'المبلغ مطلوب';
  else if (!MONEY_RE.test(p.amount.trim())) e.amount = `مبلغ غير صحيح — اكتب رقمًا صحيحًا بلا كسور ${currencyInAr(currencyOfMethod(p.method))}`;
  else if (Number(p.amount) <= 0) e.amount = 'المبلغ يجب أن يكون أكبر من صفر';
  if (p.method === 'WALLET' && p.walletFee?.trim()) {
    const fee = Number(p.walletFee.trim());
    if (!Number.isFinite(fee) || fee < 0) e.walletFee = 'رسوم المحفظة لازم تبقى رقم';
    else if (fee >= Number(p.amount)) e.walletFee = 'الرسوم لازم تبقى أقل من المبلغ اللي اتحوّل';
  }
  if (needsDestination(p.method) && !p.destination) e.destination = 'اختار الفلوس دي راحت لحساب مين';
  /**
   * **والإثبات مع الدفعة لا بعدها** (قرار المالك: «إثبات الدفع إجباري»). كان اختياريًّا
   * لأن الأدمن يطلبه عند الاعتماد على كل حال — لكن دفعةً بلا إثبات تقف في قائمة المراجعة
   * أيامًا، ويعود البياع إليها بعد أن نسي أين الصورة. ورفعُها الآن أرخص من تذكّرها غدًا.
   */
  if (p.evidence.length === 0) e.evidence = `${EVIDENCE_LABEL[p.method] ?? 'إثبات التحويل'} مطلوب مع الدفعة`;
  return e;
}

/** جسم الدفعة الأولى وحدها كما يقبله الخادم — أو null حين لا دفعة الآن. */
export function initialPaymentBody(p: InitialPaymentDraft): Record<string, unknown> | null {
  if (!p.enabled) return null;
  return partBody(p);
}

/** الأجزاء كلّها أجسامًا لـ`payments` — أو null حين لا دفعة الآن. */
export function initialPaymentsBody(p: InitialPaymentDraft): Record<string, unknown>[] | null {
  if (!p.enabled) return null;
  return paymentParts(p).map(partBody);
}

/**
 * إثباتُ كلّ جزءٍ إلى دفعته هو — بترتيب الأجزاء كما أُرسلت (والخادم يردّ معرّفاتها بالترتيب نفسه).
 * ملفٌّ في كل طلب (حدّ الخادم)، وفشلُه لا يُبطل ما حُفظ: يُعاد `false` فتقول الشاشة لصاحبه أن يرفعه
 * من تفاصيل الحجز.
 */
export async function uploadPartsEvidence(p: InitialPaymentDraft, paymentIds: string[]): Promise<boolean> {
  try {
    const parts = paymentParts(p);
    for (let i = 0; i < parts.length && i < paymentIds.length; i++) {
      for (const file of parts[i].evidence) {
        const form = new FormData();
        form.append('file', file);
        await upload(`/payments/${paymentIds[i]}/attachments`, form);
      }
    }
    return true;
  } catch { return false; }
}

function partBody(p: PaymentPart): Record<string, unknown> {
  const body: Record<string, unknown> = { method: p.method, amount: p.amount.trim() };
  if (p.method === 'CASH') body.collectedAtOffice = true;
  if (needsDestination(p.method)) body.destination = p.destination;
  if (p.method === 'WALLET' && Number(p.walletFee) > 0) body.walletFee = p.walletFee!.trim();
  return body;
}

/** ما يُحسب للحجز من جزء: المبلغ ناقص رسوم المحفظة (0166) */
const creditedOf = (x: PaymentPart) => {
  const a = Number(x.amount);
  if (!Number.isFinite(a) || a <= 0) return 0;
  const fee = x.method === 'WALLET' ? Number(x.walletFee) : 0;
  return Number.isFinite(fee) && fee > 0 && fee < a ? a - fee : a;
};

/**
 * **المتبقي = سعر البيع − المحصَّل**، محسوبًا لا مكتوبًا (قرار المالك: «يكتب اللي
 * اتحصّل والمتبقي كام»، ثم «لازم تحسبها من مصدر واحد، والأفضل البيع − المحصَّل»).
 *
 * ولو كُتب بيدٍ لصار للشركة رقمان لشيءٍ واحد: المكتوب والمحسوب. وأول ما تُسجَّل دفعةٌ
 * ثانية يصير المكتوب خطأً صامتًا لا يُلاحظه أحد. فهو يُعرض ويتغيّر مع كل رقم يُكتب،
 * ولا يُرسَل إلى الخادم أصلًا — الخادم يحسبه من دفعاته المعتمَدة.
 *
 * **وبعملة الدفعة وحدها يُقارَن**: سعر البيع بالجنيه، فدفعةٌ بالريال أو الدولار لا
 * تُطرح منه — لا سعر صرف في النظام. ويُقال ذلك صراحةً بدل أن يُعرض رقمٌ مخلوط.
 */
function Remaining({ salePrice, parts }: { salePrice: string; parts: PaymentPart[] }) {
  const sale = Number(salePrice);
  // المحصَّل بالجنيه من كل الأجزاء، وما كان بعملةٍ أخرى يُقال ولا يُطرح
  const egpParts = parts.filter(x => currencyOfMethod(x.method) === 'EGP');
  const foreign = parts.length - egpParts.length;
  const paid = egpParts.reduce((a, x) => a + creditedOf(x), 0);   // صافيًا من رسوم المحفظة

  /**
   * الخانة تظهر **دائمًا** حتى قبل كتابة سعر البيع (قرار المالك: «خلّي هنا خانة
   * للمتبقي كام؟»). وإخفاؤها حتى يُكتب السعر يجعل البياع يظنّ أن النظام لا يحسبه،
   * فيبحث عن خانةٍ يكتبه فيها بيده — وذلك بعينه ما لا نريد.
   */
  if (!Number.isFinite(sale) || sale <= 0) {
    return (
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-dashed border-slate-300 bg-white px-3 py-2 text-sm">
        <span className="text-ink-600">المتبقي: <b className="tabular-nums text-ink-400">—</b></span>
        <span className="text-xs text-ink-500">يظهر وحده بمجرّد كتابة «سعر البيع» — ولا يُكتب بيد.</span>
      </div>
    );
  }

  if (egpParts.length === 0) {
    return (
      <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        الدفعة بعملة غير الجنيه، فلا تُطرح من سعر البيع — ولا سعر صرف في النظام.
        المتبقي بالجنيه يظهر في تفاصيل الحجز بعد اعتماد الدفعة.
      </p>
    );
  }

  const collected = paid;
  const rest = sale - collected;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
      <span className="text-ink-600">سعر البيع: <b className="tabular-nums text-ink-900">{egpNum(String(sale))}</b></span>
      <span className="text-ink-600">المحصَّل: <b className="tabular-nums text-emerald-700">{egpNum(String(collected))}</b></span>
      <span className={rest > 0 ? 'text-rose-700' : 'text-emerald-700'}>
        المتبقي: <b className="text-base tabular-nums">{egpNum(String(rest))}</b> {EGP}
      </span>
      {rest < 0 && <span className="text-xs text-amber-800">المحصَّل أكبر من سعر البيع — راجع الرقم.</span>}
      {foreign > 0 && <span className="text-xs text-amber-800">وفيه جزء بعملة تانية مش مطروح من المتبقي (مفيش سعر صرف).</span>}
    </div>
  );
}

export function InitialPaymentSection({ value, onChange, errors, busy, salePrice, alwaysOn, note, destinations: given }: {
  value: InitialPaymentDraft;
  onChange: (next: InitialPaymentDraft) => void;
  errors: Record<string, string>;
  busy: boolean;
  /** سعر بيع الحجز كما يكتبه البياع الآن — به يُحسب المتبقي لحظةً بلحظة. */
  salePrice?: string;
  /** الدفعة إجبارية في هذا النوع (جديد/مكرر): يختفي مفتاح «يوجد دفع الآن». */
  alwaysOn?: boolean;
  /** جملةٌ تشرح المطلوب في هذا النوع بعينه — تُكتب من الشاشة لا هنا. */
  note?: string;
  /** «راحت لحساب مين» — من lookups الشاشة إن كانت في يدها، وإلّا تُقرأ من الخادم */
  destinations?: LookupOption[];
}) {
  const extra = value.extra ?? [];
  const destinations = usePaymentDestinations(given);
  const setMain = (part: PaymentPart) => onChange({ ...value, ...part });
  const setExtra = (i: number, part: PaymentPart) => onChange({ ...value, extra: extra.map((x, j) => (j === i ? part : x)) });
  const removeExtra = (i: number) => onChange({ ...value, extra: extra.filter((_, j) => j !== i) });
  // الجزء الجديد يقترح طريقةً غير الأولى: من دفع فيزا غالبًا يكمّل كاش، والعكس
  const addExtra = () => onChange({ ...value, extra: [...extra, emptyPaymentPart(value.method === 'CASH' ? 'VISA_OFFICE' : 'CASH')] });
  const partErrors = (i: number) => Object.fromEntries(
    Object.entries(errors).filter(([k]) => k.startsWith(`extra${i}.`)).map(([k, v]) => [k.slice(`extra${i}.`.length), v]));

  /**
   * حيث تكون الدفعة إجبارية، تُفتح الخانات من نفسها.
   *
   * وبديلُه أن يرى البياع قسمًا مطويًّا بنجمةٍ حمراء لا يعرف كيف يفتحه — ومفتاحُ
   * الفتح مخفيٌّ لأنه إجباري. فالإلزام يُفتح لا يُقفل.
   */
  useEffect(() => {
    if (alwaysOn && !value.enabled) { onChange({ ...value, enabled: true }); return; }
    /**
     * وحين يزول الإلزام (انتقل التصنيف إلى «شركة» أو «تعديل» أو «الغاء») تُطوى كما
     * فُتحت — **ما لم يكن قد كُتب فيها شيء**. فلو بقيت مفتوحةً فارغةً لطالبت بمبلغٍ
     * لا يريده أحد ومنعت الحفظ، ولو طُويت وفيها أرقامٌ لضاع ما كُتب.
     */
    if (!alwaysOn && value.enabled && !value.amount.trim() && !value.destination
        && value.evidence.length === 0 && extra.length === 0) {
      onChange({ ...value, enabled: false });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [alwaysOn]);

  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        {/* عنوان القسم «إثبات الدفع»: ما يُحسم به الحجز هو الإثبات لا ترتيب الدفعة */}
        <h3 className="text-base font-semibold text-ink-900">
          {/* النجمة علامةٌ للعين وحدها (aria-hidden): اسم العنوان يبقى «إثبات الدفع»
              كما هو، والإلزام يُقال بالكلام في الجملة تحته لا برمزٍ في الاسم. */}
          إثبات الدفع{alwaysOn && <span aria-hidden="true" className="text-rose-600"> *</span>}
        </h3>
        {/* في الجديد والمكرر الدفعة إجبارية (قرار المالك)، فلا مفتاح يُطفئها —
            ومفتاحٌ لا يُطفئ شيئًا زرٌّ يخدع من يضغطه */}
        {!alwaysOn && (
          <label className="flex items-center gap-2 text-sm text-ink-700">
            <input type="checkbox" checked={value.enabled} disabled={busy}
              onChange={e => onChange({ ...value, enabled: e.target.checked })} />
            يوجد دفع الآن
          </label>
        )}
      </header>

      {note && <p className="mt-2 text-sm text-ink-600">{note}</p>}

      {/* مكان إثبات الدفع يُقال في الحالتين لا في قسم المرفقات: المرفقات للتوثيق العام
          ولا تمنع شيئًا، وخانة الإثبات هنا داخل الدفعة نفسها. */}
      <p className="mt-1 text-xs text-ink-500">{PAYMENT_EVIDENCE_LOCATION_NOTE}</p>

      {!value.enabled ? (
        <>
          <p className="mt-2 text-sm text-ink-500">
            يمكنك حفظ الحجز بلا دفعة، وتسجيل الدفعات لاحقًا من تفاصيل الحجز.
          </p>
          {/* والمتبقي يُرى حتى قبل فتح الخانات: بلا دفعةٍ الآن المتبقي هو سعر البيع كلّه */}
          {salePrice !== undefined && <Remaining salePrice={salePrice} parts={[]} />}
        </>
      ) : (
        <div className="mt-3 space-y-3">
          <PartFields part={value} onChange={setMain} errors={errors} busy={busy} destinations={destinations} />

          {extra.map((x, i) => (
            <div key={i} className="rounded-xl border border-brand-200 bg-white p-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <b className="text-sm text-ink-800">جزء {i + 2} — بطريقة دفع تانية</b>
                {!busy && (
                  <button type="button" className="btn-ghost min-h-0 px-2 py-0.5 text-xs text-rose-700"
                    onClick={() => removeExtra(i)}>شيل الجزء ده</button>
                )}
              </div>
              <PartFields part={x} onChange={part => setExtra(i, part)} errors={partErrors(i)} busy={busy} destinations={destinations} />
            </div>
          ))}

          {1 + extra.length < MAX_PAYMENT_PARTS && (
            <button type="button" className="btn-ghost px-3 py-1.5 text-sm" disabled={busy} onClick={addExtra}>
              + جزء تاني بطريقة دفع مختلفة
            </button>
          )}

          {/* المتبقي محسوبًا من الأجزاء كلّها لحظةً بلحظة — لا يُكتب ولا يُرسَل (قرار المالك) */}
          {salePrice !== undefined && <Remaining salePrice={salePrice} parts={paymentParts(value)} />}

          <p className="text-xs text-ink-500">
            {extra.length > 0 ? <>كل جزء بيتسجّل <strong>دفعة لوحده</strong> بطريقته وإثباته، و</> : <>الدفعة تُسجَّل </>}
            <strong>بانتظار مراجعة الأدمن</strong> — لا يعتمدها البياع. وتُحفظ مع
            الحجز في عملية واحدة: إمّا الكل أو لا شيء. و«دفعة تانية» بعد كده للمتبقي بس.
          </p>
        </div>
      )}
    </section>
  );
}

/** خانات جزءٍ واحد: الطريقة والمبلغ و«راحت لحساب مين» والإثبات — للأوّل وللإضافية سواء. */
function PartFields({ part, onChange, errors, busy, destinations }: {
  part: PaymentPart; onChange: (next: PaymentPart) => void; errors: Record<string, string>; busy: boolean;
  destinations: LookupOption[];
}) {
  const [fileError, setFileError] = useState<string | null>(null);
  const set = <K extends keyof PaymentPart>(k: K, v: PaymentPart[K]) => onChange({ ...part, [k]: v });
  const currency = currencyOfMethod(part.method);
  const currencyName = currencyAr(currency);

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="طريقة الدفع" required>
          <Select options={PAYMENT_METHODS} value={part.method} disabled={busy}
            onChange={e => set('method', e.target.value as PaymentMethod)} />
        </Field>
        <Field label={`المبلغ المحصَّل (${currencyName})`} required error={errors.amount}
          hint={`${currencyInAr(currency)} الصحيح بلا كسور`}>
          <TextInput inputMode="numeric" value={part.amount} disabled={busy}
            onChange={e => set('amount', e.target.value)} />
        </Field>
        {needsDestination(part.method) && (
          <Field label={DESTINATION_LABEL} required error={errors.destination}>
            <Select options={destinations.map(d => ({ code: d.code, ar: d.labelAr }))} placeholder="اختار الحساب…"
              value={part.destination} disabled={busy} onChange={e => set('destination', e.target.value)} />
          </Field>
        )}
        {part.method === 'WALLET' && (
          <Field label="رسوم المحفظة (فودافون كاش)" error={errors.walletFee}
            hint="اللي العميل دفعه زيادة على سعر البيع — المبلغ فوق زي السكرينة، والحجز بيتحسب على الفرق">
            <TextInput inputMode="numeric" value={part.walletFee ?? ''} disabled={busy} placeholder="مثلًا 100"
              onChange={e => set('walletFee', e.target.value)} />
          </Field>
        )}
      </div>

      {currency !== 'EGP' && (
        <Alert kind="info">
          هذه الدفعة <strong>{currencyInAr(currency)}</strong> وتُسجَّل وتُعرض بعملتها كما هي.
          ولا يوجد في النظام سعر صرف معتمد، فلا تُحوَّل إلى الجنيه ولا تُخصم من المتبقي
          بالجنيه — تظهر في مجموع {currencyName} المستقلّ.
        </Alert>
      )}

      <Field label={EVIDENCE_LABEL[part.method] ?? 'إثبات التحويل'} required
        error={errors.evidence ?? fileError}
        hint="الاعتماد من الأدمن يتطلّبه — ورفعه الآن يوفّر رحلة ثانية. الصيغ: JPG · JPEG · PNG · PDF، بحد أقصى 10 ميجابايت للملف.">
        {/**
          * الاختيار **يُضاف إلى ما قبله ولا يستبدله** (مراجعة 24/09/2026، وقرار المالك 23/09: «اتأكد إنه
          * يخلّيه يرفع أكتر من صورة عادي»): البياع يصوّر الإيصال ثم يتذكّر صورة التحويل، واختيارٌ ثانٍ
          * كان يمحو الأوّل بلا كلمة. والقبول **بقواعد المرفقات نفسها** لا بفحص حجمٍ وحده.
          */}
        <input type="file" className="control" disabled={busy} multiple accept={ACCEPT_ATTR}
          onChange={e => {
            const picked = Array.from(e.target.files ?? []);
            e.target.value = '';                    // حتى يُقبل الملفّ نفسه ثانيةً بعد إزالته
            const bad = picked.map(f => ({ f, err: validateFile(f) })).find(x => x.err);
            setFileError(bad ? `«${bad.f.name}»: ${bad.err}` : null);
            const ok = picked.filter(f => !validateFile(f));
            if (ok.length) set('evidence', [...part.evidence, ...ok]);
          }} />
      </Field>
      {part.evidence.length > 0 && (
        /* القائمة خارج Field عمدًا: Field يلفّ أبناءه بـlabel، وزرٌّ داخله يدخل نصّه في اسم الخانة */
        <ul className="space-y-1" aria-label="ملفات الإثبات المختارة">
          {part.evidence.map((f, i) => (
            <li key={`${f.name}-${f.size}-${i}`}
              className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-sm">
              <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
              <span className="text-xs tabular-nums text-ink-500">{humanSize(f.size)}</span>
              {!busy && (
                <button type="button" className="btn-ghost min-h-0 px-2 py-0.5 text-xs"
                  onClick={() => set('evidence', part.evidence.filter((_, j) => j !== i))}>إزالة</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
